import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import isBefore from 'date-fns/isBefore'
import isToday from 'date-fns/isToday'
import differenceInDays from 'date-fns/differenceInDays'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { day, month, relativeTimeFuture, year } from '../../helper/date'
import translate from '../../helper/translate'
import Tooltip, { StyledTooltip } from '../general/Tooltip'
import Icon from '../general/Icon'
import { StyledFlag } from '../general/Flag'
import { desktop } from '../../styles/media'
import RegionOnlyFlag from './RegionOnlyFlag'
import Tag from '../general/Tag'

const ReleaseDate = ({ firstPrint, futurePrefix, prerelease, relative, releaseEu, releaseEuDayUnknown, releaseEuMonthUnknown, releaseInternational, releaseNa, releaseNaDayUnknown, releaseNaMonthUnknown, releasePrint, showInfo, showRegionOnlyFlag, showToday, showOverdue, t, tcgNotConfirmed, userLanguage }) => {
	const dates = {
		eu: releaseEu,
		na: releaseNa,
		print: releasePrint,
		international: releaseInternational
	}

	const getDateType = () => {
		if (!releaseInternational && !releasePrint) {
			if (userLanguage === 'en-US') {
				if (releaseNa) {
					return 'na'
				}

				return 'eu'
			} else {
				if (releaseEu) {
					return 'eu'
				}

				return 'na'
			}
		}

		if (releasePrint) {
			return 'print'
		}

		return 'international'
	}

	const dateType = getDateType()
	const date = dates[dateType]

	const future = isBefore(new Date(), new Date(date))

	let releaseStr = date ? day(date, true) : (tcgNotConfirmed ? 'TBC' : 'TBA')
	const today = showToday && date ? isToday(new Date(date)) : false
	const variousDates = date !== null && isBefore(new Date(date), new Date(1971, 0, 1))

	if (date && showOverdue && isBefore(new Date(date), new Date())) {
		const days = differenceInDays(new Date(), new Date(date))

		return t('general.limitedListDayOverdue', { count: days })
	}

	if (relative && date) {
		releaseStr = relativeTimeFuture(date, t)
	}

	if ((dateType === 'eu' && releaseEuMonthUnknown) || (dateType === 'na' && releaseNaMonthUnknown) || (dateType === 'print' && (releaseEuMonthUnknown || releaseNaMonthUnknown))) {
		releaseStr = year(date, true)
	} else if ((dateType === 'eu' && releaseEuDayUnknown) || (dateType === 'na' && releaseNaDayUnknown) || (dateType === 'print' && (releaseEuDayUnknown || releaseNaDayUnknown))) {
		releaseStr = month(date, true)
	}

	return (
		<StyledReleaseDate
			future={future && !relative}
			hasDate={!!date}
			firstPrint={firstPrint}
		>
			<Top>
				{showRegionOnlyFlag && (
					<RegionOnlyFlag releaseEu={releaseEu} releaseNa={releaseNa} />
				)}

				{relative && (
					<RelativeIcon>
						<Icon name="clock" />
					</RelativeIcon>
				)}

				{date && future && futurePrefix}
				{variousDates && !today && t('general.variousDates')}
				{!today && !variousDates && releaseStr}

				{today && (
					<Today>
						<Icon name="sparkles" />
						{t('general.today')}
					</Today>
				)}

				{!date && tcgNotConfirmed && showInfo && (
					<Tooltip title={t('general.setTcgNotConfirmedDescription')} mobileShowOnClick position="right" positionMobile="bottom">
						<Icon name="question-mark-circle" />
					</Tooltip>
				)}

				{!date && !tcgNotConfirmed && showInfo && (
					<Tooltip title={t('general.setTBADescription')} mobileShowOnClick position="right" positionMobile="bottom">
						<Icon name="question-mark-circle" />
					</Tooltip>
				)}
			</Top>

			{prerelease && (
				<Bottom>
					<Tag orange>{t('general.prerelease')}</Tag>
				</Bottom>
			)}
		</StyledReleaseDate>
	)
}

ReleaseDate.propTypes = {
	firstPrint: PropTypes.bool,
	futurePrefix: PropTypes.string,
	prerelease: PropTypes.bool,
	releaseEu: PropTypes.string,
	releaseEuDayUnknown: PropTypes.bool,
	releaseEuMonthUnknown: PropTypes.bool,
	releaseInternational: PropTypes.string,
	releaseNa: PropTypes.string,
	releaseNaDayUnknown: PropTypes.bool,
	releaseNaMonthUnknown: PropTypes.bool,
	releasePrint: PropTypes.string,
	relative: PropTypes.bool,
	showInfo: PropTypes.bool,
	showOverdue: PropTypes.bool,
	showRegionOnlyFlag: PropTypes.bool,
	showToday: PropTypes.bool,
	t: PropTypes.func.isRequired,
	tcgNotConfirmed: PropTypes.bool,
	userLanguage: PropTypes.string.isRequired
}

ReleaseDate.defaultProps = {
	firstPrint: false,
	futurePrefix: null,
	prerelease: false,
	releaseEu: null,
	releaseEuDayUnknown: false,
	releaseEuMonthUnknown: false,
	releaseInternational: null,
	releaseNa: null,
	releaseNaDayUnknown: false,
	releaseNaMonthUnknown: false,
	releasePrint: null,
	relative: false,
	showInfo: false,
	showOverdue: false,
	showRegionOnlyFlag: true,
	showToday: false,
	tcgNotConfirmed: false
}

export const StyledReleaseDate = styled.span`
	color: ${props => props.theme.textLight};
	font-size: 1rem;

	align-items: center;
	display: flex;
	width: fit-content;

	${props => props.future && `
		color: ${props.theme.green} !important;
		font-weight: bold;
	`}

	${props => props.firstPrint && !props.future && props.hasDate && `
		color: ${props.theme.primary} !important;
	`}

	${props => !props.hasDate && `
		font-size: 0.9rem;
	`}

	${StyledFlag} {
		border-radius: 2px;
		height: 16px;
		width: 16px;
	}

	${Tag} {
		margin-left: 0.35rem;
	}

	${desktop`
		align-items: flex-end;
		flex-direction: column;
		gap: 0.35rem;
	`}
`

const Top = styled.div`
	&, ${StyledTooltip} {
		align-items: center;
		display: inline-flex;
	}

	> svg, > ${StyledTooltip} {
		margin-left: 0.35rem;
	}

	${desktop`
		&, ${StyledTooltip} {
			justify-content: flex-end;
		}
	`}
`

const Bottom = styled.div`
`

const Today = styled.span`
	align-items: center;
	display: flex;

	svg {
		color: ${props => props.theme.yellow};
		margin: 0 0.25rem 0 0;
	}
`

const RelativeIcon = styled.span`
	align-items: center;
	display: flex;

	svg {
		color: ${props => props.theme.textLight};
		margin: 0 0.25rem 0 0;
		height: 14px;
		width: 14px;
	}
`

const enhance = compose(
	connect(state => ({
		userLanguage: state.persist.language
	})),
	translate('general')
)

export default enhance(ReleaseDate)
